import * as React from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { IrrigationSiteDto } from '@lib/dto/irrigation.dto'
import type { IrrigationProblemSubject } from '@lib/enums/irrigation.enum'
import {
  MAX_IRRIGATION_SYNC_OPERATIONS,
  irrigationSyncOperationSchema,
  type IrrigationSyncOperation,
} from '@lib/input/irrigation.input'
import { findIrrigationViolations } from '@lib/helpers/irrigation'
import { irrigationService } from '@/services/IrrigationService'
import { irrigationKeys } from '@/hooks/queryKeys'
import { ApiError } from '@/helpers/apiClient'
import { isRetriableStatus } from '@/helpers/retriableStatus'
import { toErrorMessage } from '@/helpers/logger'
import {
  isForVisit,
  type SurveyVisit,
  type SyncFailure,
} from '@/site-visits/surveyVisit'
import {
  applyChange,
  operationKey,
  ruleStateOf,
  toOperation,
  type IrrigationChange,
} from '@/irrigation-survey/irrigationOperations'
import {
  applyQueue,
  emptyCache,
  enqueue,
  inVisit,
  loadCache,
  saveCache,
  type IrrigationCache,
  type LoadedCache,
} from '@/irrigation-survey/irrigationOutbox'

/** Long enough to batch a burst of taps, short enough to feel immediate. */
export const FLUSH_DEBOUNCE_MS = 800
/** How often to try again while anything is owed and the last try failed. */
export const FLUSH_RETRY_MS = 20_000

/** A change the survey refused, and why, in words for the technician. */
export type CommitResult = { ok: true } | { ok: false; message: string }

export interface IrrigationSurvey {
  /** What the map draws: the server's copy with this phone's edits on top. */
  site: IrrigationSiteDto
  /** Whether the server's copy has arrived at least once (or the phone had one). */
  isReady: boolean
  /** Records with a change still waiting to reach the server, as `entity:id`. */
  pendingKeys: ReadonlySet<string>
  queuedCount: number
  isOnline: boolean
  syncError: SyncFailure | null
  /** Queued work an older build left that this one could not read back. */
  lostOperations: number
  /**
   * Whether the problems shown are known to be the working visit's, so one
   * can be recorded. False while they load (see `IrrigationCache.fresh`).
   */
  findingsReady: boolean
  /**
   * Make a change, or refuse it with the reason. `isCorrection` is the answer
   * to "Was it wrong, or did it change?" when a revisit asked it of a change
   * to the system itself, and `changedOn` the day it changed when that was
   * asked too.
   */
  commit: (
    change: IrrigationChange,
    isCorrection?: boolean,
    changedOn?: string,
  ) => CommitResult
  /** Try to send now. */
  flush: () => void
  /**
   * Throw away everything not yet synced and take the server's copy. Only
   * for a queue the server keeps refusing; the screen asks first.
   */
  discardQueue: () => void
}

const EMPTY_PENDING: ReadonlySet<string> = new Set()

/**
 * The identity of a visit for effects and memos.
 *
 * The screen builds a new visit object on every render. Keying on that
 * object rebuilt the map on every keystroke. The id, the visits joined into
 * it, and whether this phone is still waiting to hear it exists are the
 * whole of what a change of visit means.
 */
function visitIdentity(visit: SurveyVisit | null): string {
  if (visit === null) return ''
  return `${visit.pending ? '1' : '0'}:${[visit.id, ...visit.joinedIds].join(',')}`
}

function load(siteId: string): LoadedCache {
  return siteId === ''
    ? { cache: emptyCache(siteId), lostOperations: 0 }
    : loadCache(siteId)
}

/** Violations as comparable keys, so a refusal is about what an edit broke. */
function violationKeys(
  site: IrrigationSiteDto,
  appliesTo: ReadonlyMap<string, readonly IrrigationProblemSubject[]>,
): Map<string, string> {
  return new Map(
    findIrrigationViolations(ruleStateOf(site), appliesTo).map((violation) => [
      `${violation.entity}:${violation.id}:${violation.message}`,
      violation.message,
    ]),
  )
}

/**
 * An operation as it is queued: a problem on the visit it was found on, and
 * a change to the system itself naming the visit it was made in (null for
 * none being recorded in) and the answer a revisit asked for, for its
 * history. See docs/site-visits.md.
 */
function madeIn(
  built: IrrigationSyncOperation,
  visit: SurveyVisit | null,
  isCorrection: boolean | undefined,
  changedOn: string | undefined,
): IrrigationSyncOperation {
  if (built.op === 'upsertProblem') {
    return visit === null
      ? built
      : { ...built, problem: { ...built.problem, visitId: visit.id } }
  }
  return {
    ...built,
    visitId: visit?.id ?? null,
    ...(isCorrection === undefined ? {} : { isCorrection }),
    ...(changedOn === undefined ? {} : { changedOn }),
  }
}

/**
 * A site's irrigation survey: the phone's working copy, the queue of changes
 * owed to the server, and the machinery that drains it. See the tree
 * survey's hook for the shape (`useTreeSurvey`); this one differs in three
 * ways.
 *
 * An edit is checked before it is queued, with the input schema and the
 * shared rules (`findIrrigationViolations`), and refused with a reason if it
 * would break one. A batch the server refuses is refused again on every
 * retry, so the phone must never queue one. Only what the edit newly breaks
 * counts: a site whose data was already imperfect must not refuse every edit.
 *
 * Large queues are sent in pieces of at most `MAX_IRRIGATION_SYNC_OPERATIONS`.
 *
 * And a queue the server keeps refusing can be thrown away on purpose, with
 * the screen asking first.
 *
 * Mount one per site, keyed by the site id: the phone's copy and queue are
 * read once, when the survey for a site mounts. What the map draws is derived
 * on each render from the server's copy and the queue rather than copied into
 * state by an effect, so there is no moment where the two disagree.
 */
export function useIrrigationSurvey(
  siteId: string,
  /** What each problem type applies to, archived ones included. */
  problemTypeAppliesTo: ReadonlyMap<
    string,
    readonly IrrigationProblemSubject[]
  >,
  {
    visit = null,
    canRecord = visit !== null,
    canSend = true,
  }: {
    /**
     * The visit being worked in: whose problems the map shows and where a
     * new one goes. Null when the site has none for irrigation.
     */
    visit?: SurveyVisit | null
    /**
     * Whether problems can be recorded into `visit`. False shows its problems
     * read-only, and the rest of the system can still be changed.
     */
    canRecord?: boolean
    /**
     * False while a visit started on this phone has not reached the server;
     * nothing is sent until it has (see `useTreeSurvey`).
     */
    canSend?: boolean
  } = {},
): IrrigationSurvey {
  const queryClient = useQueryClient()
  const visitId = visit?.id ?? null
  const query = useQuery({
    queryKey: irrigationKeys.site(siteId, visitId),
    queryFn: () => irrigationService.getSite(siteId, visitId),
    // Only once the visit has reached the server: before, it would answer
    // that there is no such visit, and not be asked again.
    enabled: siteId !== '' && canSend,
    retry: false,
    staleTime: 30_000,
  })
  // The server's copy only once it is of the visit being worked in.
  const server =
    query.data !== undefined && isForVisit(query.data.visitId, visit)
      ? query.data
      : undefined
  const [loaded] = React.useState(() => load(siteId))
  const [cache, setCache] = React.useState<IrrigationCache>(() => loaded.cache)
  const [syncError, setSyncError] = React.useState<SyncFailure | null>(null)
  const [isOnline, setIsOnline] = React.useState(() => navigator.onLine)
  const [isFlushing, setIsFlushing] = React.useState(false)

  /*
   * The visit the map is drawing, kept stable across renders that rebuild
   * the same visit. Adjusted while rendering, which is how a value derived
   * from props stays put when the object is new but the visit is not: an
   * effect would be a render too late, and a fresh object every render
   * rebuilds the map on every keystroke.
   */
  const [shownVisit, setShownVisit] = React.useState(visit)
  const visitKey = visitIdentity(visit)
  if (visitIdentity(shownVisit) !== visitKey) {
    setShownVisit(visit)
  }

  /*
   * What the map draws. The server's copy, when there is one, with every
   * queued change for this visit layered on top; otherwise whatever the
   * phone last had. Problems queued for another visit stay in the queue and
   * off this screen.
   */
  const site = React.useMemo(
    () =>
      server === undefined
        ? cache.site
        : applyQueue(server, cache.queue, cache.site, shownVisit),
    [server, cache, shownVisit],
  )

  const findingsReady =
    visit !== null &&
    (server !== undefined ||
      (cache.fresh === true && cache.visitId === visit.id))

  // Read by callbacks, which must never see a stale queue, copy or rule set.
  // Synced after each render rather than written during it.
  const cacheRef = React.useRef(cache)
  const siteRef = React.useRef(site)
  const appliesToRef = React.useRef(problemTypeAppliesTo)
  const onlineRef = React.useRef(isOnline)
  const visitRef = React.useRef(visit)
  const canSendRef = React.useRef(canSend)
  const canRecordRef = React.useRef(canRecord)
  const findingsReadyRef = React.useRef(findingsReady)
  React.useLayoutEffect(() => {
    cacheRef.current = cache
    siteRef.current = site
    appliesToRef.current = problemTypeAppliesTo
    onlineRef.current = isOnline
    visitRef.current = visit
    canSendRef.current = canSend
    canRecordRef.current = canRecord
    findingsReadyRef.current = findingsReady
  })

  /* -- the visit being worked in ------------------------------------- */
  // A different visit shows its own problems: the ones still queued for it,
  // until the server's copy arrives. The same visit under its kept id is
  // only relabeled.
  React.useEffect(() => {
    // Read through the ref, synced by the layout effect above before this
    // runs: keyed by the visit's ids, so a new object for the same visit is
    // no change.
    setCache((current) =>
      inVisit(current, visitRef.current),
    )
  }, [visitKey])

  /* -- persist ------------------------------------------------------- */
  React.useEffect(() => {
    // Fresh once the server's copy of the visit has been seen (see `fresh`).
    if (siteId !== '') {
      saveCache(siteId, {
        ...cache,
        site,
        ...(server === undefined ? {} : { fresh: true }),
      })
    }
  }, [siteId, cache, site, server])

  /* -- online / offline ---------------------------------------------- */
  React.useEffect(() => {
    function goOnline(): void {
      setIsOnline(true)
    }
    function goOffline(): void {
      setIsOnline(false)
    }
    window.addEventListener('online', goOnline)
    window.addEventListener('offline', goOffline)
    return () => {
      window.removeEventListener('online', goOnline)
      window.removeEventListener('offline', goOffline)
    }
  }, [])

  /* -- flushing ------------------------------------------------------ */
  const flushingRef = React.useRef(false)
  const flush = React.useCallback((): void => {
    if (siteId === '' || flushingRef.current || !onlineRef.current) return
    if (!canSendRef.current) return
    const pending = [...cacheRef.current.queue].sort((a, b) => a.seq - b.seq)
    if (pending.length === 0) return
    /*
     * Claim the oldest batch's worth. Anything past it, and anything recorded
     * while the request is in flight, has a higher sequence number and is
     * left for the next flush, which the queue-changed effect starts as soon
     * as this one lands.
     */
    const claimed = pending.slice(0, MAX_IRRIGATION_SYNC_OPERATIONS)
    const highWaterMark = claimed.reduce(
      (max, queued) => Math.max(max, queued.seq),
      0,
    )
    flushingRef.current = true
    setIsFlushing(true)
    void (async (): Promise<void> => {
      try {
        const asked = visitRef.current?.id ?? null
        const result = await irrigationService.sync(
          siteId,
          claimed.map((queued) => queued.operation),
          asked,
        )
        setSyncError(null)
        // Taken as the working set only if the visit has not changed while
        // the request was out; the queue is retired either way.
        const current = isForVisit(result.visitId, visitRef.current)
        setCache((cached) => {
          const remaining = cached.queue.filter(
            (queued) => queued.seq > highWaterMark,
          )
          return {
            ...cached,
            site: current
              ? applyQueue(result, remaining, siteRef.current, visitRef.current)
              : cached.site,
            queue: remaining,
          }
        })
        if (current) {
          queryClient.setQueryData(irrigationKeys.site(siteId, asked), result)
        }
      } catch (caught) {
        // The queue is untouched, so a failure defers work rather than losing it.
        setSyncError({
          message: toErrorMessage(caught),
          needsAttention:
            caught instanceof ApiError && !isRetriableStatus(caught.status),
        })
      } finally {
        flushingRef.current = false
        setIsFlushing(false)
      }
    })()
  }, [siteId, queryClient])

  // Also as soon as a visit started here reaches the server.
  React.useEffect(() => {
    if (cache.queue.length === 0 || isFlushing) return undefined
    const timer = window.setTimeout(flush, FLUSH_DEBOUNCE_MS)
    return () => {
      window.clearTimeout(timer)
    }
  }, [cache.queue, isFlushing, canSend, flush])

  React.useEffect(() => {
    if (cache.queue.length === 0) return undefined
    const timer = window.setInterval(flush, FLUSH_RETRY_MS)
    return () => {
      window.clearInterval(timer)
    }
  }, [cache.queue.length, flush])

  React.useEffect(() => {
    if (isOnline) flush()
  }, [isOnline, flush])

  /* -- changes ------------------------------------------------------- */
  const commit = React.useCallback(
    (
      change: IrrigationChange,
      isCorrection?: boolean,
      changedOn?: string,
    ): CommitResult => {
      const working = visitRef.current
      const built = toOperation(change)
      if (!built.ok) return built
      const operationDraft = built.operation
      // A problem is a finding, and a finding belongs to a visit: one recorded
      // with none open would have nowhere to go.
      if (
        operationDraft.op === 'upsertProblem' &&
        (working === null || !canRecordRef.current)
      ) {
        return {
          ok: false,
          message: 'Start a visit to record what you find.',
        }
      }
      // Recorded only into a view known to be this visit's own: see `fresh`.
      if (operationDraft.op === 'upsertProblem' && !findingsReadyRef.current) {
        return {
          ok: false,
          message:
            "This visit's problems are still loading. Try again in a moment.",
        }
      }
      const operation = madeIn(
        operationDraft,
        canRecordRef.current ? working : null,
        isCorrection,
        changedOn,
      )
      const parsed = irrigationSyncOperationSchema.safeParse(operation)
      if (!parsed.success) {
        return {
          ok: false,
          message:
            parsed.error.issues[0]?.message ?? 'That is not complete yet.',
        }
      }
      const current = siteRef.current
      const next = applyChange(current, change)
      const before = violationKeys(current, appliesToRef.current)
      const introduced = [...violationKeys(next, appliesToRef.current)].find(
        ([key]) => !before.has(key),
      )
      if (introduced !== undefined) {
        return { ok: false, message: introduced[1] }
      }
      const problemId =
        parsed.data.op === 'upsertProblem' ? parsed.data.problem.id : undefined
      const recorded =
        problemId === undefined
          ? undefined
          : next.problems.find((item) => item.id === problemId)
      const updated = {
        ...enqueue(
          cacheRef.current,
          parsed.data,
          ...(recorded === undefined ? [] : [recorded]),
        ),
        site: next,
      }
      // Two commits in one tick must each see the other.
      cacheRef.current = updated
      siteRef.current = next
      setCache(updated)
      return { ok: true }
    },
    [],
  )

  const discardQueue = React.useCallback((): void => {
    setCache((current) => ({
      ...current,
      queue: [],
      site: server ?? current.site,
    }))
    setSyncError(null)
    void queryClient.invalidateQueries({
      queryKey: irrigationKeys.sites(siteId),
    })
  }, [server, queryClient, siteId])

  const pendingKeys = React.useMemo<ReadonlySet<string>>(
    () =>
      cache.queue.length === 0
        ? EMPTY_PENDING
        : new Set(cache.queue.map((queued) => operationKey(queued.operation))),
    [cache.queue],
  )

  return React.useMemo(
    () => ({
      site,
      isReady:
        server !== undefined || cache.queue.length > 0 || hasAnyRecord(site),
      pendingKeys,
      queuedCount: cache.queue.length,
      isOnline,
      syncError,
      lostOperations: loaded.lostOperations,
      findingsReady,
      commit,
      flush,
      discardQueue,
    }),
    [
      site,
      server,
      cache.queue.length,
      pendingKeys,
      isOnline,
      syncError,
      loaded.lostOperations,
      findingsReady,
      commit,
      flush,
      discardQueue,
    ],
  )
}

function hasAnyRecord(site: IrrigationSiteDto): boolean {
  return (
    site.waterSources.length +
      site.timers.length +
      site.zones.length +
      site.equipment.length +
      site.problems.length >
    0
  )
}
